import { TeamOutlined } from "@ant-design/icons";
import { redirect } from "@tanstack/react-router";
import { Spin } from "antd";
import { useGetUserQuery } from "api/TenantSlug/User/hooks.ts";
import { ManageAffiliateForm } from "components/Forms/ManageAffiliateForm/ManageAffiliateForm.tsx";
import { AffiliatesTable } from "components/Tables/AffiliatesTable/AffiliatesTable.tsx";
import { DrawerContent } from "components/UI/DrawerContent/DrawerContent.tsx";
import { TenantPage } from "components/UI/TenantPage/TenantPage.tsx";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { usePermission } from "hooks/usePermissions.ts";
import { getSessionFromStorage } from "hooks/useSession.ts";
import { useUser } from "hooks/useUser.ts";
import { PermissionEnum } from "types/enums.ts";
import { FormId } from "types/models.ts";
import { createTenantFileRoute } from "utils/createTenantFileRoute.ts";
import { getAffiliateHeaderItems } from "utils/tenantHeaderItems.ts";

export const Route = createTenantFileRoute("/_authenticated/$tenantSlug/_layout/affiliate/_layout/")({
    component: AffiliatePage,
    staticData: {
        permissions: [PermissionEnum.AffiliateManage, PermissionEnum.AffiliatePayoutView],
        redirectPermissions: [PermissionEnum.AffiliatePayoutView],
        showInSideMenu: true,
        label: "pages.affiliate.title",
        icon: <TeamOutlined />,
        sort: 4,
    },
    loader: ({ context: { t }, params }) => {
        const { user } = getSessionFromStorage();
        const tenant = user?.tenants?.find((tenant) => tenant.slug_name === params.tenantSlug);
        const permissions = tenant?.userPermissions?.map((p) => p.name) ?? [];

        const hasManage = permissions.includes(PermissionEnum.AffiliateManage);
        const hasPayoutView = permissions.includes(PermissionEnum.AffiliatePayoutView);

        if (!hasManage && hasPayoutView) {
            throw redirect({
                to: "/$tenantSlug/affiliate/request/payout",
                params: { tenantSlug: params.tenantSlug },
                replace: true,
            });
        }

        return {
            breadcrumb: t("pages.affiliate.title"),
        };
    },
});

export function AffiliatePage() {
    const { t: tC } = usePaldockTranslation("common");
    const permissionUtils = usePermission();
    const userUtils = useUser();
    const canViewForeign = permissionUtils.hasPermission(PermissionEnum.RoleViewForeign);
    const authenticatedUser = userUtils.getAuthenticatedUser();
    const selfUserQuery = useGetUserQuery(
        authenticatedUser?.id ?? "",
        { filter: { include: "tenantSettings" } },
        {
            enabled: !canViewForeign && !!authenticatedUser?.id,
            refetchOnMount: "always",
            staleTime: 0,
        }
    );

    if (canViewForeign) {
        return (
            <TenantPage headerNavigationItems={getAffiliateHeaderItems}>
                <AffiliatesTable />
            </TenantPage>
        );
    }

    if (!authenticatedUser?.id) {
        return <>{tC("errors.no_tenant_id")}</>;
    }

    if (selfUserQuery.isLoading || !selfUserQuery.data?.user) {
        return (
            <TenantPage headerNavigationItems={getAffiliateHeaderItems} isEdit={true} saveFormId={FormId.AddUserForm}>
                <div className="flex justify-center items-center h-64">
                    <Spin size="large" />
                </div>
            </TenantPage>
        );
    }

    return (
        <TenantPage headerNavigationItems={getAffiliateHeaderItems} isEdit={true} saveFormId={FormId.AddUserForm}>
            <div className="w-full max-w-[1000px] mx-auto">
                <DrawerContent>
                    <ManageAffiliateForm mode="detail" user={selfUserQuery.data.user} />
                </DrawerContent>
            </div>
        </TenantPage>
    );
}
